import React from 'react';
import { X, Keyboard } from 'lucide-react';

interface ShortcutReferencePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  {
    group: 'Navigation',
    items: [
      { keys: ['Alt', '1-9'], description: 'Switch to tab by position' },
      { keys: ['Alt', '0'], description: 'Jump to Output tab' },
      { keys: ['\u2190', '\u2192'], description: 'Navigate tabs (when tab bar focused)' },
      { keys: ['Home'], description: 'First tab (when tab bar focused)' },
      { keys: ['End'], description: 'Last tab (when tab bar focused)' },
    ],
  },
  {
    group: 'Actions',
    items: [
      { keys: ['Ctrl', 'Enter'], description: 'Generate (Studio tab only)' },
      { keys: ['Ctrl', 'Z'], description: 'Undo (Studio/Vocals/Production)' },
      { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo (Studio/Vocals/Production)' },
      { keys: ['Ctrl', ','], description: 'Toggle Settings' },
      { keys: ['Ctrl', 'Shift', 'L'], description: 'Toggle Template Library' },
      { keys: ['Ctrl', 'Shift', 'M'], description: 'Toggle Session Memory' },
    ],
  },
  {
    group: 'General',
    items: [
      { keys: ['Ctrl', '/'], description: 'Toggle this panel' },
      { keys: ['Esc'], description: 'Close modals / panels' },
    ],
  },
];

export const ShortcutReferencePanel: React.FC<ShortcutReferencePanelProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[61] w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent text-slate-950">
              <Keyboard size={16} />
            </div>
            <h2 className="text-sm font-black text-slate-50 uppercase tracking-tight">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-50 hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Close shortcuts panel"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {shortcuts.map((group) => (
            <div key={group.group}>
              <h3 className="text-[11px] font-black text-accent uppercase tracking-widest mb-3">
                {group.group}
              </h3>
              <div className="space-y-2">
                {group.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-slate-300">{item.description}</span>
                    <div className="flex items-center gap-1">
                      {item.keys.map((key, ki) => (
                        <React.Fragment key={ki}>
                          {ki > 0 && <span className="text-slate-400 text-[11px]">+</span>}
                          <kbd className="px-2 py-0.5 text-[11px] font-mono font-bold text-slate-300 bg-slate-800 border border-slate-700 rounded-md shadow-sm">
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
